/**
 * PL-2 question review (spec §F–§G, 2026-10-20) — shown on a benchmark run
 * while it is 'drafting' or 'draft'.
 *
 * BLIP proposes the questions and QCs them; Aurora edits, replaces, rejects,
 * regenerates, accepts flags or records exceptions, and finally approves the
 * whole set — exactly five per stage (2026-10-21). Rejections need a reason,
 * and every replaced wording stays in the history panel. Nothing on this page sends a question to the model panel —
 * approval is the only way research starts, and only an admin or manager can
 * approve or record an exception.
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  BenchmarkPrompt,
  BuyerJourneyStage,
  PromptHistoryEntry,
  PromptPersona,
  PromptQcFinding,
  PromptRejectReason,
  PromptReviewPayload,
  PromptArchitecture,
} from '@aurora/shared';
import {
  BUYER_STAGE_LABELS,
  BUYER_STAGE_ORDER,
  parseImportedQuestions,
  PROMPT_ARCHITECTURE_LABELS,
  PROMPT_ARCHITECTURES,
  PROMPT_PERSONA_LABELS,
  PROMPT_PERSONAS,
  PROMPT_QC_RULE_LABELS,
  PROMPT_REJECT_REASON_LABELS,
  SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE,
  SOURCE_INTELLIGENCE_DATASET_DEFAULT,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';
import { MarketSelect } from './MarketSelect';
import { useAuth } from '../auth/AuthContext';

const STAGES: Array<{ key: BuyerJourneyStage; label: string; hint: string }> = [
  {
    key: 'problem_definition',
    label: 'Stage 1 — Problem Definition',
    hint: 'A scientific problem or unexplained result. No instrument, vendor or purchase.',
  },
  {
    key: 'workflow_evaluation',
    label: 'Stage 2 — Workflow / Experimental Approach',
    hint: 'Methods, protocols and workflow trade-offs. No manufacturers or brands.',
  },
  {
    key: 'technology_evaluation',
    label: 'Stage 3 — Technology / Instrument Evaluation',
    hint: 'Technology types, capabilities and limits. Never which companies.',
  },
  {
    key: 'supplier_evaluation',
    label: 'Stage 4 — Supplier Evaluation',
    hint: 'Unaided "which manufacturers offer…, how do they compare". Names none.',
  },
  {
    key: 'supplier_selection',
    label: 'Stage 5 — Supplier Selection',
    hint: 'Unaided: ask the AI to recommend specific products or systems and their manufacturers, with the criteria, reasons and trade-offs. Names none.',
  },
];

const STATUS_LABEL: Record<string, string> = {
  pass: 'PASS',
  review: 'REVIEW',
  block: 'BLOCK',
  suggestion: 'OPTIONAL',
};

const STAGE_SHORT: Record<BuyerJourneyStage, string> = {
  problem_definition: 'Stage 1',
  workflow_evaluation: 'Stage 2',
  technology_evaluation: 'Stage 3',
  supplier_evaluation: 'Stage 4',
  supplier_selection: 'Stage 5',
};

const HISTORY_ACTION_LABEL: Record<PromptHistoryEntry['action'], string> = {
  rejected: 'Rejected',
  regenerated: 'Replaced (regenerated)',
  edited: 'Edited (previous wording)',
  discarded: 'Discarded (regenerate all)',
};

const REJECT_REASONS = Object.keys(
  PROMPT_REJECT_REASON_LABELS,
) as PromptRejectReason[];

function isResolved(p: BenchmarkPrompt, f: PromptQcFinding): boolean {
  return (p.qcResolutions ?? []).some(
    (r) =>
      r.rule === f.rule &&
      (!r.findingExplanation || r.findingExplanation === f.explanation) &&
      (r.kind === 'exception' ||
        (r.kind === 'accepted' && f.severity === 'review')),
  );
}

function errMessage(e: unknown): string {
  return e instanceof ApiClientError ? e.message : 'Request failed';
}

const csv = (raw: string): string[] =>
  raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

export function PromptReview({
  runId,
  onApproved,
}: {
  runId: string;
  onApproved: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const canApprove = user?.role === 'admin' || user?.role === 'manager';
  const [data, setData] = useState<PromptReviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [editing, setEditing] = useState<Record<string, string>>({});
  const [instruction, setInstruction] = useState<Record<string, string>>({});
  const [exceptionFor, setExceptionFor] = useState<{
    pid: string;
    rule: string;
  } | null>(null);
  const [exceptionReason, setExceptionReason] = useState('');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scope, setScope] = useState({
    categories: '',
    applications: '',
    exclusions: '',
  });
  const [scopePersonas, setScopePersonas] = useState<PromptPersona[]>([]);
  const [designation, setDesignation] = useState({
    si: false,
    datasetId: SOURCE_INTELLIGENCE_DATASET_DEFAULT,
    market: '',
    architecture: 'PL-2' as PromptArchitecture,
  });
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [addText, setAddText] = useState<Record<string, string>>({});
  const [rejectFor, setRejectFor] = useState<{
    pid: string;
    code: PromptRejectReason | '';
    note: string;
  } | null>(null);
  const [confirmRegenAll, setConfirmRegenAll] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [filling, setFilling] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      setData(await api.getPromptReview(runId));
      setError(null);
    } catch (e) {
      setError(errMessage(e));
    }
  }, [runId]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    if (data?.run.status !== 'drafting') return;
    const t = setInterval(() => void load(), 4000);
    return () => clearInterval(t);
  }, [data?.run.status, load]);
  useEffect(() => {
    if (!data) return;
    setScope({
      categories: data.run.productCategories.join(', '),
      applications: data.run.applications.join(', '),
      exclusions: (data.run.scopeExclusions ?? []).join(', '),
    });
    setScopePersonas(data.run.personas as PromptPersona[]);
    setDesignation({
      si: data.run.purpose === 'source_intelligence',
      datasetId:
        data.run.datasetId ??
        SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE[data.run.promptArchitecture ?? 'PL-2'],
      market: data.run.datasetMarket ?? '',
      architecture: data.run.promptArchitecture ?? 'PL-2',
    });
  }, [data?.run.id, data?.run.updatedAt]);

  /** Run one action; the server returns the refreshed draft. */
  const act = async (
    key: string,
    fn: () => Promise<PromptReviewPayload>,
  ): Promise<PromptReviewPayload | null> => {
    setBusy(key);
    setError(null);
    try {
      const next = await fn();
      setData(next);
      return next;
    } catch (e) {
      setError(errMessage(e));
      return null;
    } finally {
      setBusy(null);
    }
  };

  if (!data) {
    return error ? (
      <div className="form-error">{error}</div>
    ) : (
      <div className="muted centered">Loading questions…</div>
    );
  }

  const { run, prompts, blockers } = data;
  const drafting = run.status === 'drafting';
  // PL-1 questions are Aurora's own: BLIP never drafts or rewrites them, so
  // every generator button is hidden (the server refuses them too).
  const pl1 = run.promptArchitecture === 'PL-1';
  const libFindings = run.promptQc?.findings ?? [];
  const libResolved = (f: PromptQcFinding): boolean =>
    (run.promptQc?.resolutions ?? []).some(
      (r) =>
        r.rule === f.rule &&
        (!r.findingExplanation || r.findingExplanation === f.explanation),
    );
  // The question badges alone read "25 pass" while set-level findings still
  // hold approval (2026-10-21), so the open ones are counted in the header too.
  const openLib = libFindings.filter((f) => f.severity !== 'suggestion' && !libResolved(f)).length;
  const counts = { pass: 0, review: 0, block: 0 } as Record<string, number>;
  for (const p of prompts)
    if (p.qcStatus) counts[p.qcStatus] = (counts[p.qcStatus] ?? 0) + 1;

  const approve = async (): Promise<void> => {
    const next = await act('approve', () => api.approvePromptLibrary(runId));
    if (next) onApproved();
  };

  const target = data.stageTarget ?? 5;
  const history = data.history ?? [];
  const exclusions = data.exclusionEnforcement ?? [];

  /** Generate one question at a time until the stage has `target`. */
  const fillStage = async (stage: BuyerJourneyStage, missing: number) => {
    for (let i = 0; i < missing; i += 1) {
      setFilling(`${stage}:${i + 1}/${missing}`);
      const next = await act(`fill-${stage}`, () =>
        api.addPrompt(runId, { stage }),
      );
      if (!next) break;
    }
    setFilling(null);
  };

  return (
    <div className="prompt-review">
      <div className="card prompt-review-head">
        <div>
          <h2>
            Question review{' '}
            <span className="muted">
              {PROMPT_ARCHITECTURE_LABELS[run.promptArchitecture ?? 'PL-2']} ·
              revision {run.promptLibraryRevision ?? 1}
            </span>
          </h2>
          <p className="field-hint">
            Proposed questions only — no AI research has run. Fix or accept
            every flag, then approve the whole set to start the research run.
            Approval locks the exact text; a later change becomes a new
            revision.
          </p>
          <div className="qc-counts">
            <span className="qc-badge qc-pass">{counts.pass} pass</span>
            <span className="qc-badge qc-review">{counts.review} review</span>
            <span className="qc-badge qc-block">{counts.block} block</span>
            {openLib > 0 && (
              <span className="qc-badge qc-review">
                {openLib} set-level open
              </span>
            )}
            <span className="muted">
              {prompts.length} questions · drafting cost $
              {(run.usage?.generator.cost ?? 0).toFixed(3)}
            </span>
          </div>
        </div>
        <div className="head-actions">
          <button
            type="button"
            className="btn"
            disabled={drafting || busy !== null}
            onClick={() => void act('qc', () => api.rerunPromptQc(runId))}
          >
            {busy === 'qc' ? 'Checking…' : 'Re-run QC'}
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy !== null}
            onClick={() => setScopeOpen((v) => !v)}
          >
            Scope
          </button>
          {pl1 && (
            <button
              type="button"
              className={prompts.length === 0 ? 'btn btn-primary' : 'btn'}
              disabled={busy !== null || drafting}
              onClick={() => setImportOpen((v) => !v)}
              title="Replace every question with Aurora's pasted set."
            >
              Import questions
            </button>
          )}
          {pl1 ? (
            // A PL-1 run stuck in checking (e.g. a lost job after a revision)
            // is released and re-checked; nothing is regenerated.
            drafting &&
            Date.now() - Date.parse(run.updatedAt) >= 960_000 && (
              <button
                type="button"
                className="btn"
                disabled={busy !== null}
                onClick={() =>
                  void act('recheck', () => api.regenerateAllPrompts(runId))
                }
              >
                {busy === 'recheck' ? 'Checking…' : 'Re-check questions'}
              </button>
            )
          ) : confirmRegenAll ? (
            <span className="inline-confirm">
              Discard all {prompts.length} questions? They stay in the history.{' '}
              <button
                type="button"
                className="btn btn-small btn-danger"
                disabled={busy !== null}
                onClick={() =>
                  void act('regen-all', () =>
                    api.regenerateAllPrompts(runId),
                  ).then(() => setConfirmRegenAll(false))
                }
              >
                {busy === 'regen-all' ? 'Discarding…' : 'Yes, regenerate all'}
              </button>{' '}
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setConfirmRegenAll(false)}
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="btn"
              disabled={
                busy !== null ||
                (drafting && Date.now() - Date.parse(run.updatedAt) < 960_000)
              }
              onClick={() => setConfirmRegenAll(true)}
              title="Discard every question and draft a new set from the current scope."
            >
              Regenerate all
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary"
            disabled={
              !canApprove || drafting || blockers.length > 0 || busy !== null
            }
            onClick={() => void approve()}
            title={
              canApprove
                ? 'Lock this question set and start the research run.'
                : 'Only an admin or manager can approve a question set.'
            }
          >
            {busy === 'approve' ? 'Approving…' : 'Approve & start research'}
          </button>
        </div>
      </div>

      {pl1 && importOpen && (
        <ImportPanel
          text={importText}
          onText={setImportText}
          perStage={target}
          existing={prompts.length}
          busy={busy === 'import'}
          onCancel={() => setImportOpen(false)}
          onImport={() =>
            void act('import', () => api.importPrompts(runId, importText)).then(
              (next) => {
                if (next) {
                  setImportOpen(false);
                  setImportText('');
                }
              },
            )
          }
        />
      )}
      {error && <div className="form-error">{error}</div>}
      {!drafting && blockers.length > 0 && (
        <div className="form-notice">
          <strong>Approve is waiting on:</strong>
          <ul className="approval-blockers">
            {blockers.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          {openLib > 0 && (
            <span className="muted">
              Accept or fix the set-level findings in “Library checks” below.
            </span>
          )}
        </div>
      )}
      {drafting && (
        <div className="form-notice">
          Drafting and checking questions… this page refreshes automatically.
        </div>
      )}
      {run.error && !drafting && <div className="form-error">{run.error}</div>}

      {scopeOpen && (
        <div className="card">
          <h3>Approved scope</h3>
          <p className="field-hint">
            Questions are written only from this scope. Change it here, then
            regenerate. Company and competitors are tracked, never written into
            questions.
          </p>
          {(
            [
              ['categories', 'Product categories'],
              ['applications', 'Applications'],
              ['exclusions', 'Excluded topics'],
            ] as const
          ).map(([k, label]) => (
            <div className="field" key={k}>
              <label htmlFor={`scope-${k}`}>{label} (comma-separated)</label>
              <input
                id={`scope-${k}`}
                value={scope[k]}
                onChange={(e) =>
                  setScope((s) => ({ ...s, [k]: e.target.value }))
                }
              />
            </div>
          ))}
          <div className="persona-checkboxes">
            {PROMPT_PERSONAS.map((p) => (
              <label key={p} className="persona-checkbox">
                <input
                  type="checkbox"
                  checked={scopePersonas.includes(p)}
                  onChange={() =>
                    setScopePersonas((cur) =>
                      cur.includes(p)
                        ? cur.filter((x) => x !== p)
                        : [...cur, p],
                    )
                  }
                />
                {PROMPT_PERSONA_LABELS[p]}
              </label>
            ))}
          </div>
          <div className="field">
            <label>Questions</label>
            <div className="persona-checkboxes">
              {PROMPT_ARCHITECTURES.map((a) => (
                <label key={a} className="persona-checkbox">
                  <input
                    type="radio"
                    name="scope-architecture"
                    checked={designation.architecture === a}
                    onChange={() =>
                      setDesignation((d) => ({
                        ...d,
                        architecture: a,
                        // The dataset follows the architecture unless it was
                        // deliberately renamed.
                        datasetId:
                          d.datasetId.trim() ===
                          SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE[d.architecture]
                            ? SOURCE_INTELLIGENCE_DATASET_BY_ARCHITECTURE[a]
                            : d.datasetId,
                      }))
                    }
                  />
                  {PROMPT_ARCHITECTURE_LABELS[a]}
                </label>
              ))}
            </div>
            {designation.architecture !== (run.promptArchitecture ?? 'PL-2') && (
              <p className="field-hint">
                Saving discards the current {prompts.length} questions (kept in
                the history):{' '}
                {designation.architecture === 'PL-1'
                  ? 'the draft then waits for Aurora’s questions (Import questions).'
                  : 'BLIP then drafts a new PL-2 set.'}
              </p>
            )}
          </div>
          <div className="field">
            <label className="persona-checkbox">
              <input
                type="checkbox"
                checked={designation.si}
                onChange={(e) =>
                  setDesignation((d) => ({ ...d, si: e.target.checked }))
                }
              />
              Source Intelligence run (one market of an industry dataset —
              requires web-enabled models)
            </label>
            {designation.si && (
              <div className="si-fields">
                <label htmlFor="scope-dataset">Dataset ID</label>
                <input
                  id="scope-dataset"
                  value={designation.datasetId}
                  onChange={(e) =>
                    setDesignation((d) => ({ ...d, datasetId: e.target.value }))
                  }
                />
                <label htmlFor="scope-market">Market</label>
                <MarketSelect
                  id="scope-market"
                  value={designation.market}
                  onChange={(market) =>
                    setDesignation((d) => ({ ...d, market }))
                  }
                />
              </div>
            )}
          </div>
          <div className="head-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                busy !== null ||
                drafting ||
                (designation.si &&
                  (!designation.datasetId.trim() || !designation.market.trim()))
              }
              onClick={() =>
                void act('scope', () =>
                  api.updatePromptScope(runId, {
                    productCategories: csv(scope.categories),
                    applications: csv(scope.applications),
                    personas: scopePersonas,
                    scopeExclusions: csv(scope.exclusions),
                    ...(designation.architecture !== (run.promptArchitecture ?? 'PL-2')
                      ? { promptArchitecture: designation.architecture }
                      : {}),
                    // Only a deliberate (re)designation is sent: an
                    // undesignated draft stays undesignated on a scope save.
                    ...(designation.si
                      ? {
                          purpose: 'source_intelligence' as const,
                          datasetId: designation.datasetId.trim(),
                          datasetMarket: designation.market.trim(),
                        }
                      : data.run.purpose === 'source_intelligence'
                        ? { purpose: 'client_benchmark' as const }
                        : {}),
                  }),
                )
              }
            >
              Save scope
            </button>
          </div>
        </div>
      )}

      {(libFindings.length > 0 || exclusions.length > 0) && (
        <div className="card">
          <h3>Library checks</h3>
          {libFindings.map((f, i) => (
            <div
              key={i}
              className={`qc-finding qc-${libResolved(f) ? 'resolved' : f.severity}`}
            >
              <span className={`qc-badge qc-${f.severity}`}>
                {f.rule} {STATUS_LABEL[f.severity]}
              </span>{' '}
              <strong>{PROMPT_QC_RULE_LABELS[f.rule]}:</strong> {f.explanation}
              {f.suggestedFix && (
                <span className="muted"> — {f.suggestedFix}</span>
              )}
              {f.rule === 'COUNT' ||
              (f.rule === 'R7' && f.trigger === 'duplicate' && f.severity === 'block') ? (
                <span className="muted"> (must be fixed — cannot be accepted)</span>
              ) : f.severity === 'suggestion' ? (
                <span className="muted"> (optional — does not hold up approval)</span>
              ) : libResolved(f) ? (
                <span className="muted"> (accepted)</span>
              ) : (
                f.severity === 'review' && (
                  <button
                    type="button"
                    className="btn btn-small"
                    disabled={busy !== null}
                    onClick={() =>
                      void act(`lib-${i}`, () =>
                        api.resolveLibraryFinding(runId, {
                          rule: f.rule,
                          kind: 'accepted',
                          explanation: f.explanation,
                        }),
                      )
                    }
                  >
                    Accept
                  </button>
                )
              )}
            </div>
          ))}
          {exclusions.length > 0 && (
            <div className="exclusion-enforcement">
              <strong>Excluded topics:</strong>
              <ul>
                {exclusions.map((x) => (
                  <li key={x.term}>
                    “{x.term}” —{' '}
                    {x.enforcedBy === 'family' ? (
                      <>blocks any {x.family} question (synonyms included)</>
                    ) : (
                      <span className="muted">matched word for word only</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {STAGES.map((stage) => {
        const inStage = prompts.filter((p) => p.stage === stage.key);
        const n = inStage.length;
        return (
          <div className="card" key={stage.key}>
            <h3>
              {stage.label}{' '}
              <span className={`qc-badge qc-${n === target ? 'pass' : 'block'}`}>
                {n} / {target}
              </span>
              {!drafting && !pl1 && n < target && (
                <button
                  type="button"
                  className="btn btn-small"
                  disabled={busy !== null}
                  onClick={() => void fillStage(stage.key, target - n)}
                  title={`Generate ${target - n} more question${target - n === 1 ? '' : 's'} for this stage.`}
                >
                  {filling?.startsWith(`${stage.key}:`)
                    ? `Writing ${filling.split(':')[1]}…`
                    : `Fill to ${target}`}
                </button>
              )}
              {n > target && (
                <span className="muted">
                  {' '}
                  reject {n - target} to approve
                </span>
              )}
            </h3>
            <p className="field-hint">{stage.hint}</p>
            {inStage.map((p) => {
              const isEditing = editing[p.id] !== undefined;
              const open = (p.qcFindings ?? []).filter(
                (f) => !isResolved(p, f),
              );
              return (
                <div
                  key={p.id}
                  className={`prompt-row qc-row-${p.qcStatus ?? 'none'}`}
                >
                  <div className="prompt-row-head">
                    <span className="prompt-id">P{p.ordinal}</span>
                    <span className={`qc-badge qc-${p.qcStatus ?? 'review'}`}>
                      {STATUS_LABEL[p.qcStatus ?? ''] ?? 'UNCHECKED'}
                    </span>
                    <span className="muted prompt-tags">
                      {[
                        p.productCategory,
                        p.application,
                        p.persona
                          ? PROMPT_PERSONA_LABELS[p.persona as PromptPersona]
                          : null,
                        p.origin && p.origin !== 'generated' ? p.origin : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                  {isEditing ? (
                    <textarea
                      className="prompt-edit"
                      value={editing[p.id]}
                      rows={3}
                      onChange={(e) =>
                        setEditing((m) => ({ ...m, [p.id]: e.target.value }))
                      }
                    />
                  ) : (
                    <p className="prompt-text">{p.prompt}</p>
                  )}
                  {(p.qcFindings ?? []).map((f, i) => (
                    <div
                      key={i}
                      className={`qc-finding qc-${isResolved(p, f) ? 'resolved' : f.severity}`}
                    >
                      <span className={`qc-badge qc-${f.severity}`}>
                        {f.rule} {STATUS_LABEL[f.severity]}
                      </span>{' '}
                      <strong>{PROMPT_QC_RULE_LABELS[f.rule]}:</strong>{' '}
                      {f.explanation}
                      {f.trigger && (
                        <span className="muted"> [“{f.trigger}”]</span>
                      )}
                      <span className="muted"> ({f.source})</span>
                      {f.suggestedFix && (
                        <div className="qc-fix">
                          Suggested: {f.suggestedFix}{' '}
                          {f.suggestedFix.length > 40 && !isEditing && (
                            <button
                              type="button"
                              className="btn btn-small"
                              onClick={() =>
                                setEditing((m) => ({
                                  ...m,
                                  [p.id]: f.suggestedFix ?? p.prompt,
                                }))
                              }
                            >
                              Use as edit
                            </button>
                          )}
                        </div>
                      )}
                      {isResolved(p, f) ? (
                        <span className="muted">
                          {' '}
                          —{' '}
                          {(p.qcResolutions ?? []).find(
                            (r) => r.rule === f.rule,
                          )?.kind === 'exception'
                            ? `exception: ${(p.qcResolutions ?? []).find((r) => r.rule === f.rule)?.reason ?? ''}`
                            : 'accepted'}{' '}
                          <button
                            type="button"
                            className="btn btn-small"
                            disabled={busy !== null}
                            onClick={() =>
                              void act(`clear-${p.id}`, () =>
                                api.resolvePromptFinding(runId, p.id, {
                                  rule: f.rule,
                                  kind: 'clear',
                                }),
                              )
                            }
                          >
                            Undo
                          </button>
                        </span>
                      ) : f.severity === 'suggestion' ? (
                        <span className="muted">
                          {' '}
                          — optional wording idea; no action needed
                        </span>
                      ) : f.severity === 'review' ? (
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={busy !== null}
                          onClick={() =>
                            void act(`acc-${p.id}`, () =>
                              api.resolvePromptFinding(runId, p.id, {
                                rule: f.rule,
                                kind: 'accepted',
                              }),
                            )
                          }
                        >
                          Accept
                        </button>
                      ) : (
                        canApprove && (
                          <button
                            type="button"
                            className="btn btn-small"
                            onClick={() => {
                              setExceptionFor({ pid: p.id, rule: f.rule });
                              setExceptionReason('');
                            }}
                          >
                            Record exception…
                          </button>
                        )
                      )}
                      {exceptionFor?.pid === p.id &&
                        exceptionFor.rule === f.rule && (
                          <div className="exception-form">
                            <input
                              value={exceptionReason}
                              onChange={(e) =>
                                setExceptionReason(e.target.value)
                              }
                              placeholder="Why Aurora authorizes this exception (recorded with your name)"
                            />
                            <button
                              type="button"
                              className="btn btn-small"
                              disabled={
                                exceptionReason.trim().length < 5 ||
                                busy !== null
                              }
                              onClick={() =>
                                void act(`exc-${p.id}`, () =>
                                  api.resolvePromptFinding(runId, p.id, {
                                    rule: f.rule,
                                    kind: 'exception',
                                    reason: exceptionReason.trim(),
                                  }),
                                ).then(() => setExceptionFor(null))
                              }
                            >
                              Record
                            </button>
                            <button
                              type="button"
                              className="btn btn-small"
                              onClick={() => setExceptionFor(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                    </div>
                  ))}
                  <div className="prompt-actions">
                    {isEditing ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-small btn-primary"
                          disabled={
                            busy !== null ||
                            (editing[p.id] ?? '').trim().length < 10
                          }
                          onClick={() =>
                            void act(`edit-${p.id}`, () =>
                              api.editPrompt(runId, p.id, {
                                prompt: (editing[p.id] ?? '').trim(),
                              }),
                            ).then((next) => {
                              if (next)
                                setEditing((m) => {
                                  const { [p.id]: _drop, ...rest } = m;
                                  return rest;
                                });
                            })
                          }
                        >
                          {busy === `edit-${p.id}`
                            ? 'Checking…'
                            : 'Save & re-check'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-small"
                          onClick={() =>
                            setEditing((m) => {
                              const { [p.id]: _drop, ...rest } = m;
                              return rest;
                            })
                          }
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={busy !== null || drafting}
                          onClick={() =>
                            setEditing((m) => ({ ...m, [p.id]: p.prompt }))
                          }
                        >
                          Edit
                        </button>
                        <input
                          className="regen-instruction"
                          value={instruction[p.id] ?? ''}
                          onChange={(e) =>
                            setInstruction((m) => ({
                              ...m,
                              [p.id]: e.target.value,
                            }))
                          }
                          placeholder={
                            open.length
                              ? 'Optional steer for a replacement'
                              : 'Optional steer'
                          }
                        />
                        {!pl1 && (
                          <button
                            type="button"
                            className="btn btn-small"
                            disabled={busy !== null || drafting}
                            onClick={() =>
                              void act(`regen-${p.id}`, () =>
                                api.regeneratePrompt(
                                  runId,
                                  p.id,
                                  instruction[p.id]?.trim() || undefined,
                                ),
                              )
                            }
                          >
                            {busy === `regen-${p.id}` ? 'Writing…' : 'Regenerate'}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-small btn-danger"
                          disabled={busy !== null || drafting}
                          onClick={() =>
                            setRejectFor({ pid: p.id, code: '', note: '' })
                          }
                        >
                          Reject…
                        </button>
                      </>
                    )}
                  </div>
                  {rejectFor?.pid === p.id && (
                    <div className="reject-form">
                      <select
                        aria-label="Why reject this question"
                        value={rejectFor.code}
                        onChange={(e) =>
                          setRejectFor({
                            ...rejectFor,
                            code: e.target.value as PromptRejectReason,
                          })
                        }
                      >
                        <option value="">Why reject it?</option>
                        {REJECT_REASONS.map((r) => (
                          <option key={r} value={r}>
                            {PROMPT_REJECT_REASON_LABELS[r]}
                          </option>
                        ))}
                      </select>
                      <input
                        maxLength={500}
                        value={rejectFor.note}
                        onChange={(e) =>
                          setRejectFor({ ...rejectFor, note: e.target.value })
                        }
                        placeholder={
                          rejectFor.code === 'other'
                            ? 'Say why (required for Other)'
                            : 'Note (optional)'
                        }
                      />
                      <button
                        type="button"
                        className="btn btn-small btn-danger"
                        disabled={
                          busy !== null ||
                          !rejectFor.code ||
                          (rejectFor.code === 'other' &&
                            !rejectFor.note.trim())
                        }
                        onClick={() =>
                          void act(`rej-${p.id}`, () =>
                            api.rejectPrompt(runId, p.id, {
                              reasonCode: rejectFor.code as PromptRejectReason,
                              note: rejectFor.note.trim() || undefined,
                            }),
                          ).then((next) => {
                            if (next) setRejectFor(null);
                          })
                        }
                      >
                        {busy === `rej-${p.id}` ? 'Rejecting…' : 'Reject'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => setRejectFor(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            {!drafting && pl1 && (
              <div className="si-fields">
                <input
                  value={addText[stage.key] ?? ''}
                  onChange={(e) =>
                    setAddText((m) => ({ ...m, [stage.key]: e.target.value }))
                  }
                  placeholder="Add one of Aurora's questions to this stage"
                />
                <button
                  type="button"
                  className="btn btn-small"
                  disabled={busy !== null || (addText[stage.key] ?? '').trim().length < 10}
                  onClick={() =>
                    void act(`add-${stage.key}`, () =>
                      api.addPrompt(runId, {
                        stage: stage.key,
                        prompt: (addText[stage.key] ?? '').trim(),
                      }),
                    ).then((next) => {
                      if (next) setAddText((m) => ({ ...m, [stage.key]: '' }));
                    })
                  }
                >
                  {busy === `add-${stage.key}` ? 'Adding…' : '+ Add question'}
                </button>
              </div>
            )}
            {!drafting && !pl1 && (
              <button
                type="button"
                className="btn btn-small"
                disabled={busy !== null}
                onClick={() =>
                  void act(`add-${stage.key}`, () =>
                    api.addPrompt(runId, { stage: stage.key }),
                  )
                }
              >
                {busy === `add-${stage.key}`
                  ? 'Writing…'
                  : '+ Generate another question'}
              </button>
            )}
          </div>
        );
      })}

      {history.length > 0 && (
        <div className="card">
          <h3>
            <button
              type="button"
              className="btn btn-small"
              onClick={() => setHistoryOpen((v) => !v)}
              aria-expanded={historyOpen}
            >
              {historyOpen ? 'Hide' : 'Show'}
            </button>{' '}
            Rejected &amp; replaced <span className="muted">({history.length})</span>
          </h3>
          {historyOpen && (
            <ul className="prompt-history">
              {history.map((h) => (
                <li key={h.id}>
                  <div className="muted">
                    {HISTORY_ACTION_LABEL[h.action]} · {STAGE_SHORT[h.stage]} · P
                    {h.ordinal}
                    {h.reasonCode &&
                      ` · ${PROMPT_REJECT_REASON_LABELS[h.reasonCode]}`}
                    {h.reasonNote && ` — “${h.reasonNote}”`} ·{' '}
                    {h.actorEmail ?? 'unknown'} ·{' '}
                    {new Date(h.at).toLocaleString()}
                  </div>
                  <p className="prompt-text">{h.prompt}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Paste Aurora's own questions (from Word or Excel) and replace the draft.
 * The preview uses the server's parser, so a paste that would be refused is
 * refused here first, with the reason.
 */
function ImportPanel(props: {
  text: string;
  onText: (t: string) => void;
  perStage: number;
  existing: number;
  busy: boolean;
  onCancel: () => void;
  onImport: () => void;
}) {
  const parsed = props.text.trim()
    ? parseImportedQuestions(props.text, props.perStage)
    : null;
  const usable = parsed !== null && parsed.problems.length === 0;
  return (
    <div className="card">
      <h3>Import questions</h3>
      <p className="field-hint">
        Paste all {props.perStage * 5} questions: one per line in stage order (
        {props.perStage} per stage), or under stage headings, or rows copied
        from Excel with a stage column. Numbering is ignored.
        {props.existing > 0 &&
          ` The ${props.existing} current questions are replaced and kept in the history.`}
      </p>
      <textarea
        rows={12}
        value={props.text}
        onChange={(e) => props.onText(e.target.value)}
        placeholder={'Stage 1 – Problem Definition\n1. …\n2. …'}
        style={{ width: '100%' }}
      />
      {parsed && (
        <div className="qc-counts">
          {BUYER_STAGE_ORDER.map((stage) => {
            const n = parsed.questions.filter((q) => q.stage === stage).length;
            return (
              <span
                key={stage}
                className={`qc-badge qc-${n === props.perStage ? 'pass' : 'block'}`}
              >
                {BUYER_STAGE_LABELS[stage]}: {n}
              </span>
            );
          })}
          <span className="muted">
            {parsed.stagesFrom === 'headings'
              ? 'stages from your headings'
              : parsed.stagesFrom === 'order'
                ? `stages by position (${props.perStage} each)`
                : ''}
          </span>
        </div>
      )}
      {parsed && parsed.problems.length > 0 && (
        <ul className="approval-blockers">
          {parsed.problems.map((p) => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      <div className="head-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!usable || props.busy}
          onClick={props.onImport}
        >
          {props.busy
            ? 'Importing…'
            : `Replace with these ${parsed?.questions.length ?? 0} questions`}
        </button>
        <button type="button" className="btn" onClick={props.onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
